import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

interface Thickness {
  id: string;
  value: number;
  unit: string;
}

export function ThicknessConfig() {
  const [thicknesses, setThicknesses] = useState<Thickness[]>([
    { id: "1", value: 1.0, unit: "mm" },
    { id: "2", value: 1.5, unit: "mm" },
    { id: "3", value: 2.0, unit: "mm" },
  ]);
  const [newThickness, setNewThickness] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const { toast } = useToast();

  const handleAdd = () => {
    if (!newThickness.trim()) {
      toast({
        title: "Erro",
        description: "O valor da espessura não pode estar vazio.",
        variant: "destructive",
      });
      return;
    }

    const value = parseFloat(newThickness);
    if (isNaN(value)) {
      toast({
        title: "Erro",
        description: "A espessura deve ser um número válido.",
        variant: "destructive",
      });
      return;
    }

    if (thicknesses.some(thickness => thickness.value === value)) {
      toast({
        title: "Erro",
        description: "Já existe uma espessura com este valor.",
        variant: "destructive",
      });
      return;
    }

    setThicknesses([...thicknesses, { id: Date.now().toString(), value, unit: "mm" }]);
    setNewThickness("");
    toast({
      title: "Sucesso",
      description: "Espessura adicionada com sucesso.",
    });
  };

  const handleEdit = (id: string, newValue: string) => {
    const value = parseFloat(newValue);
    if (isNaN(value)) {
      toast({
        title: "Erro",
        description: "A espessura deve ser um número válido.",
        variant: "destructive",
      });
      return;
    }

    if (thicknesses.some(thickness => thickness.value === value && thickness.id !== id)) {
      toast({
        title: "Erro",
        description: "Já existe uma espessura com este valor.",
        variant: "destructive",
      });
      return;
    }

    setThicknesses(thicknesses.map(thickness => 
      thickness.id === id ? { ...thickness, value } : thickness
    ));
    setEditingId(null);
    toast({
      title: "Sucesso",
      description: "Espessura atualizada com sucesso.",
    });
  };

  const handleDelete = (id: string) => {
    setThicknesses(thicknesses.filter(thickness => thickness.id !== id));
    toast({
      title: "Sucesso",
      description: "Espessura removida com sucesso.",
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex gap-4">
        <Input
          placeholder="Nova espessura (mm)"
          value={newThickness}
          onChange={(e) => setNewThickness(e.target.value)}
          className="max-w-sm bg-[#222831] text-white border-[#404040]"
          type="number"
          step="0.1"
        />
        <Button onClick={handleAdd} className="bg-[#0073B7] hover:bg-[#004080] text-white">
          <Plus className="h-4 w-4 mr-2" />
          Adicionar
        </Button>
      </div>

      <div className="bg-[#1A1F2C] rounded-md border border-[#404040]">
        <div className="divide-y divide-[#404040]">
          {thicknesses.map((thickness) => (
            <div key={thickness.id} className="p-4 flex items-center justify-between text-white hover:bg-[#222831]">
              {editingId === thickness.id ? (
                <Input
                  defaultValue={thickness.value}
                  type="number"
                  step="0.1"
                  onBlur={(e) => handleEdit(thickness.id, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleEdit(thickness.id, e.currentTarget.value);
                    }
                  }}
                  className="max-w-sm bg-[#222831] text-white border-[#404040]"
                  autoFocus
                />
              ) : (
                <span>{thickness.value} {thickness.unit}</span>
              )}
              
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setEditingId(thickness.id)}
                  className="hover:bg-[#222831]"
                >
                  <Pencil className="h-4 w-4 text-[#0073B7]" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(thickness.id)}
                  className="hover:bg-[#222831]"
                >
                  <Trash2 className="h-4 w-4 text-[#E60026]" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}